import { API_CONFIG, API_ENDPOINTS, apiService } from '../../config/api';
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser';
import type { EncryptedMessage, KeyWrap, WrappedConversationKey, WrappedPrivateKey } from './chatCrypto';

/**
 * REST + SSE client for the E2E-encrypted researcher chat (backend routes/chat.js, all under
 * /api/chat). The server only ever sees ciphertext envelopes and wrapped key material; this
 * module moves those blobs verbatim — encryption/decryption lives in chatCrypto.ts.
 *
 * REST calls ride apiService (auth header, retries, JSON error -> thrown Error carrying
 * `.status`). The study stream is hand-rolled SSE over fetch, mirroring copilotService:
 * EventSource cannot send the Authorization header, so the stream is read from
 * `response.body` with a chunk-safe frame parser.
 */

export type ChatMemberRole = 'researcher' | 'participant';

export interface Conversation {
  id: string;
  studyId: string;
  participantId: string;
  members: Record<string, ChatMemberRole>;
  keyVersion: number;
  /** Oldest epoch whose grants may still be served; version 1 for conversations never reset. */
  minimumReadableKeyVersion: number;
  createdAt?: string;
  lastMessageAt: string | null;
  lastMessageSenderId: string | null;
  needsRotation: boolean;
  rotationRequiredAt: string | null;
  /** Every member's read marker ({ uid: lastReadAt ISO }); present on listConversations. */
  readState?: Record<string, string>;
}

/** A stored ciphertext envelope as the server returns it (EncryptedMessage + routing metadata). */
export interface ChatMessage extends EncryptedMessage {
  /** The client-chosen messageId (idempotency key) doubles as the stored id. */
  id: string;
  senderId: string;
  senderRole: ChatMemberRole;
  createdAt: string;
}

/** Envelope for POST /messages: the EncryptedMessage plus the client-generated idempotency id. */
export interface OutgoingChatMessage extends EncryptedMessage {
  messageId: string;
}

/** One of the requester's own usable wrapped-conversation-key grants. */
export interface MyKeyGrant {
  granteeUid: string;
  keyVersion: number;
  wrappedCk: WrappedConversationKey;
  wrappedBy: string;
  granteePublicKey: string;
}

/** A previous identity key of a member (append-only history; drives `keyChangePending`). */
export interface KeyHistoryEntry {
  publicKey: string;
  replacedAt: string;
}

/** A conversation member as seen by the grant-scan UI. */
export interface MemberKeyInfo {
  uid: string;
  role: ChatMemberRole;
  /** Researcher e-mail (the uid when unavailable) or "Participant <id>"; null on participant routes. */
  displayName: string | null;
  /** null until the member publishes a key. */
  publicKey: string | null;
  /** Key versions the member already holds grants for under their CURRENT public key. */
  keyVersions: number[];
  keyHistory: KeyHistoryEntry[];
  /** Backend-computed: an old-key grant exists here and no grant is bound to the current key. */
  keyChangePending: boolean;
}

export interface ConversationKeys {
  myKeys: MyKeyGrant[];
  members: MemberKeyInfo[];
}

export interface KeyGrantUpload {
  granteeUid: string;
  granteePublicKey: string;
  wrappedCk: WrappedConversationKey;
}

export interface GrantUploadResult {
  created: string[];
  skipped: string[];
}

export interface EncryptionResetResult {
  conversationId: string;
  previousKeyVersion: number;
  newKeyVersion: number;
  resetAt: string;
}

/** Passkey metadata as served by GET /keys/me and GET /passkeys — never a wrap or counter. */
export interface PasskeyInfo {
  credentialId: string;
  name: string;
  transports: string[];
  createdAt: string;
  /** null until the first successful assertion ("Never used"). */
  lastUsedAt: string | null;
}

/** The researcher's own key doc; wrappedPrivateKey is only ever served to its owner. */
export interface UserKeys {
  uid: string;
  publicKey: string;
  /** The recovery-key wrap. */
  wrappedPrivateKey?: WrappedPrivateKey;
  /** Standard base64 of the 32-byte PRF input. */
  prfSalt: string;
  trustEpoch: number;
  keyHistory: KeyHistoryEntry[];
  passkeys: PasskeyInfo[];
  createdAt?: string;
  updatedAt?: string;
}

/** The exact setup/reset upload body for POST /api/chat/keys. */
export interface PublishKeysBody {
  publicKey: string;
  wrappedPrivateKey: WrappedPrivateKey;
  prfSalt: string;
  passkey: {
    name: string;
    registration: RegistrationResponseJSON;
    wrap: KeyWrap;
  };
}

export type SecurityEventType =
  | 'emergency_encryption_reset'
  | 'identity_key_replaced'
  | 'member_added'
  | 'member_removed';

/** Metadata-only security event (events routes + SSE `security_event`). */
export interface SecurityEvent {
  id: string;
  type: SecurityEventType;
  actorUid: string;
  subjectUid?: string | null;
  /** Resolved at read time; "A former member" when unresolvable; null on participant routes. */
  subjectDisplayName?: string | null;
  fromKeyVersion?: number | null;
  toKeyVersion?: number | null;
  fromFingerprint?: string | null;
  toFingerprint?: string | null;
  createdAt: string;
}

/** The route's maximum page size; events are fetched once per conversation open at this limit. */
export const EVENTS_MAX_LIMIT = 200;

export interface ReadStateEntry {
  uid: string;
  lastReadAt: string;
}

export interface UnreadSummary {
  conversations: Array<{ conversationId: string; unread: boolean }>;
  unreadCount: number;
}

/** Provision a conversation per enrolled participant and refresh every members map. */
export async function ensureConversations(studyId: string): Promise<Conversation[]> {
  const response = await apiService.post(API_ENDPOINTS.CHAT.ENSURE_CONVERSATIONS(studyId));
  return Array.isArray(response?.conversations) ? response.conversations : [];
}

/** The study's conversations, most recently active first, each with its readState map. */
export async function listConversations(studyId: string): Promise<Conversation[]> {
  const response = await apiService.get(API_ENDPOINTS.CHAT.CONVERSATIONS(studyId));
  return Array.isArray(response?.conversations) ? response.conversations : [];
}

/** A page of ciphertext envelopes, oldest-first within the page. `before` is an ISO timestamp. */
export async function listMessages(
  studyId: string,
  conversationId: string,
  options: { limit?: number; before?: string } = {},
): Promise<ChatMessage[]> {
  const params = new URLSearchParams();
  if (options.limit != null) params.set('limit', String(options.limit));
  if (options.before) params.set('before', options.before);
  const query = params.toString();
  const response = await apiService.get(
    `${API_ENDPOINTS.CHAT.MESSAGES(studyId, conversationId)}${query ? `?${query}` : ''}`,
  );
  return Array.isArray(response?.messages) ? response.messages : [];
}

/**
 * Append a ciphertext envelope. The messageId is the idempotency key: a duplicate resend
 * returns the already-stored message (server 200 vs 201 — indistinguishable here by design).
 */
export async function sendMessage(
  studyId: string,
  conversationId: string,
  envelope: OutgoingChatMessage,
): Promise<ChatMessage> {
  const response = await apiService.post(API_ENDPOINTS.CHAT.MESSAGES(studyId, conversationId), envelope);
  return response.message;
}

/** Tolerate listings that predate the additive member fields. */
function normalizeMember(raw: any): MemberKeyInfo {
  return {
    uid: raw.uid,
    role: raw.role,
    displayName: typeof raw.displayName === 'string' ? raw.displayName : null,
    publicKey: typeof raw.publicKey === 'string' ? raw.publicKey : null,
    keyVersions: Array.isArray(raw.keyVersions) ? raw.keyVersions : [],
    keyHistory: Array.isArray(raw.keyHistory) ? raw.keyHistory : [],
    keyChangePending: raw.keyChangePending === true,
  };
}

/** The caller's own usable grants plus every member's public key / granted key versions. */
export async function getConversationKeys(studyId: string, conversationId: string): Promise<ConversationKeys> {
  const response = await apiService.get(API_ENDPOINTS.CHAT.CONVERSATION_KEYS(studyId, conversationId));
  return {
    myKeys: Array.isArray(response?.myKeys) ? response.myKeys : [],
    members: Array.isArray(response?.members) ? response.members.map(normalizeMember) : [],
  };
}

/** Upload HPKE-wrapped conversation keys for other members (each wrapped for their CURRENT key). */
export async function uploadGrants(
  studyId: string,
  conversationId: string,
  grants: KeyGrantUpload[],
  options: { bootstrap?: boolean } = {},
): Promise<GrantUploadResult> {
  const body = options.bootstrap ? { grants, bootstrap: true } : { grants };
  const response = await apiService.post(API_ENDPOINTS.CHAT.KEY_GRANTS(studyId, conversationId), body);
  return {
    created: Array.isArray(response?.created) ? response.created : [],
    skipped: Array.isArray(response?.skipped) ? response.skipped : [],
  };
}

/**
 * Irreversibly start a fresh encryption epoch for one conversation. The confirmation flag is
 * fixed here rather than accepted from arbitrary callers; the owner UI performs typed confirmation
 * before invoking this function and the server independently enforces ownership and exact shape.
 * `grants` is the owner's bootstrap set for `expectedKeyVersion + 1`, landed in the same transaction.
 */
export async function resetConversationEncryption(
  studyId: string,
  conversationId: string,
  expectedKeyVersion: number,
  grants: KeyGrantUpload[],
): Promise<EncryptionResetResult> {
  return apiService.post(API_ENDPOINTS.CHAT.ENCRYPTION_RESET(studyId, conversationId), {
    expectedKeyVersion,
    confirmHistoryLoss: true,
    grants,
  });
}

/** Ordered security events for a conversation (oldest first). */
export async function listSecurityEvents(
  studyId: string,
  conversationId: string,
  options: { limit?: number; before?: string } = {},
): Promise<SecurityEvent[]> {
  const params = new URLSearchParams();
  if (options.limit != null) params.set('limit', String(options.limit));
  if (options.before) params.set('before', options.before);
  const query = params.toString();
  const response = await apiService.get(
    `${API_ENDPOINTS.CHAT.EVENTS(studyId, conversationId)}${query ? `?${query}` : ''}`,
  );
  return Array.isArray(response?.events) ? response.events : [];
}

function normalizeUserKeys(raw: any): UserKeys {
  return {
    ...raw,
    prfSalt: typeof raw?.prfSalt === 'string' ? raw.prfSalt : '',
    trustEpoch: typeof raw?.trustEpoch === 'number' ? raw.trustEpoch : 1,
    keyHistory: Array.isArray(raw?.keyHistory) ? raw.keyHistory : [],
    passkeys: Array.isArray(raw?.passkeys) ? raw.passkeys : [],
  };
}

/** Publish the identity (public key, recovery-key wrap, prfSalt, first passkey) in one upload. */
export async function publishKeys(body: PublishKeysBody): Promise<UserKeys> {
  const response = await apiService.post(API_ENDPOINTS.CHAT.PUBLISH_KEYS, body);
  return normalizeUserKeys(response.keys);
}

/**
 * The researcher's own key doc (including their wrappedPrivateKey). Resolves null while
 * unpublished (server 404) so callers branch into key setup instead of catching.
 */
export async function getMyKeys(): Promise<UserKeys | null> {
  try {
    const response = await apiService.get(API_ENDPOINTS.CHAT.MY_KEYS);
    return normalizeUserKeys(response.keys);
  } catch (error: any) {
    if (error?.status === 404) return null;
    throw error;
  }
}

/** Re-wrap the same private key under a new recovery key (never a key publish). */
export async function rewrapRecoveryKey(wrappedPrivateKey: WrappedPrivateKey): Promise<UserKeys> {
  const response = await apiService.put(API_ENDPOINTS.CHAT.RECOVERY_KEY, { wrappedPrivateKey });
  return normalizeUserKeys(response.keys);
}

/** "Sign out of all other trusted browsers": increments and returns the new trustEpoch. */
export async function bumpTrustEpoch(): Promise<number> {
  const response = await apiService.post(API_ENDPOINTS.CHAT.TRUST_EPOCH, {});
  return response.trustEpoch;
}

// ---------------------------------------------------------------------------
// Passkeys (WebAuthn) — the wire calls only; ceremonies live in chatPasskeys.ts
// ---------------------------------------------------------------------------

export type PasskeyRegistrationPurpose = 'setup' | 'add' | 'identity_reset';

export async function getPasskeyRegistrationOptions(body: {
  prfSalt: string;
  purpose: PasskeyRegistrationPurpose;
}): Promise<PublicKeyCredentialCreationOptionsJSON> {
  const response = await apiService.post(API_ENDPOINTS.CHAT.PASSKEY_REGISTER_OPTIONS, body);
  return response.options;
}

export async function registerPasskey(body: {
  name: string;
  registration: RegistrationResponseJSON;
  wrap: KeyWrap;
}): Promise<PasskeyInfo> {
  const response = await apiService.post(API_ENDPOINTS.CHAT.PASSKEY_REGISTER, body);
  return response.passkey;
}

export async function getPasskeyAssertionOptions(): Promise<PublicKeyCredentialRequestOptionsJSON> {
  const response = await apiService.post(API_ENDPOINTS.CHAT.PASSKEY_ASSERT_OPTIONS, {});
  return response.options;
}

/** The only route that ever returns a wrap — the asserted credential's. */
export async function assertPasskey(
  assertion: AuthenticationResponseJSON,
): Promise<{ credentialId: string; wrap: KeyWrap }> {
  const response = await apiService.post(API_ENDPOINTS.CHAT.PASSKEY_ASSERT, { assertion });
  return { credentialId: response.credentialId, wrap: response.wrap };
}

export async function listPasskeys(): Promise<PasskeyInfo[]> {
  const response = await apiService.get(API_ENDPOINTS.CHAT.PASSKEYS);
  return Array.isArray(response?.passkeys) ? response.passkeys : [];
}

export async function removePasskey(credentialId: string): Promise<void> {
  await apiService.delete(API_ENDPOINTS.CHAT.PASSKEY(credentialId));
}

/** Mark the conversation read for the caller. Empty body — lastReadAt is the server's clock. */
export async function markRead(studyId: string, conversationId: string): Promise<ReadStateEntry> {
  const response = await apiService.put(API_ENDPOINTS.CHAT.READ_STATE(studyId, conversationId));
  return response.readState;
}

/** Badge feed computed from conversation metadata only — never message content. */
export async function getUnread(studyId: string): Promise<UnreadSummary> {
  const response = await apiService.get(API_ENDPOINTS.CHAT.UNREAD(studyId));
  return {
    conversations: Array.isArray(response?.conversations) ? response.conversations : [],
    unreadCount: typeof response?.unreadCount === 'number' ? response.unreadCount : 0,
  };
}

// ---------------------------------------------------------------------------
// Realtime study stream (SSE over fetch)
// ---------------------------------------------------------------------------

export interface StreamMessageEvent {
  conversationId: string;
  message: ChatMessage;
}

/** Conversation METADATA only — never message content. */
export interface StreamConversationEvent {
  conversationId: string;
  keyVersion: number;
  minimumReadableKeyVersion: number;
  lastMessageAt: string | null;
  lastMessageSenderId: string | null;
  needsRotation: boolean;
}

export interface StreamReadStateEvent {
  conversationId: string;
  uid: string;
  lastReadAt: string;
}

export interface StreamSecurityEvent {
  conversationId: string;
  event: SecurityEvent;
}

/** A grant for the requesting uid landed — metadata only, never a wrappedCk. */
export interface StreamMemberKeyEvent {
  conversationId: string;
  granteeUid: string;
  keyVersion: number;
}

export interface StreamHandlers {
  /** One `init` per (re)connect seeds the conversations the caller is a member of. */
  onInit?: (conversations: Conversation[]) => void;
  onMessage?: (event: StreamMessageEvent) => void;
  onConversation?: (event: StreamConversationEvent) => void;
  onReadState?: (event: StreamReadStateEvent) => void;
  onSecurityEvent?: (event: StreamSecurityEvent) => void;
  onMemberKey?: (event: StreamMemberKeyEvent) => void;
  /**
   * Fired when a connection attempt fails, the read errors, or the server ends the stream
   * with a `stream_error` frame. Purely informational — a reconnect is already scheduled
   * (with backoff) unless the subscription was unsubscribed/aborted.
   */
  onStreamError?: (message: string) => void;
}

export interface StreamOptions {
  /** Aborting stops the current connection AND all future reconnects. */
  signal?: AbortSignal;
}

export interface StreamSubscription {
  /** Aborts the in-flight connection and cancels any pending reconnect. */
  unsubscribe: () => void;
}

const STREAM_INITIAL_BACKOFF_MS = 1_000;
const STREAM_MAX_BACKOFF_MS = 30_000;

type FrameKind = 'event' | 'ignored' | 'stream_error';

/**
 * Parse one SSE frame and dispatch to the matching handler. Comment lines (heartbeats such as
 * `: keepalive`) and unknown/malformed frames are ignored. Returns what the frame was so the
 * connection loop can reset its backoff on real events and treat `stream_error` as a
 * server-initiated end.
 */
function dispatchStreamFrame(raw: string, handlers: StreamHandlers): FrameKind {
  let event = '';
  const dataLines: string[] = [];
  for (const line of raw.split('\n')) {
    if (line.startsWith(':')) continue; // comment frame — the server's keepalive heartbeat
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
  }
  if (!event) return 'ignored';
  const data = dataLines.join('\n');

  if (event === 'stream_error') {
    // Generic by design (the server never leaks listener internals); the response ends after it.
    let message = 'Chat stream interrupted';
    try {
      const payload = JSON.parse(data);
      if (typeof payload?.error === 'string' && payload.error) message = payload.error;
    } catch {
      /* keep the generic message */
    }
    handlers.onStreamError?.(message);
    return 'stream_error';
  }

  let payload: any;
  try {
    payload = data ? JSON.parse(data) : undefined;
  } catch {
    return 'ignored'; // a malformed frame is non-fatal; the stream keeps flowing
  }

  if (event === 'init') {
    if (payload && Array.isArray(payload.conversations)) handlers.onInit?.(payload.conversations);
    return 'event';
  }
  if (event === 'message') {
    if (payload?.conversationId && payload.message) handlers.onMessage?.(payload as StreamMessageEvent);
    return 'event';
  }
  if (event === 'conversation') {
    if (payload?.conversationId) handlers.onConversation?.(payload as StreamConversationEvent);
    return 'event';
  }
  if (event === 'read_state') {
    if (payload?.conversationId && payload.uid) handlers.onReadState?.(payload as StreamReadStateEvent);
    return 'event';
  }
  if (event === 'security_event') {
    if (payload?.conversationId && payload.event?.type) handlers.onSecurityEvent?.(payload as StreamSecurityEvent);
    return 'event';
  }
  if (event === 'member_key') {
    if (payload?.conversationId && payload.granteeUid) handlers.onMemberKey?.(payload as StreamMemberKeyEvent);
    return 'event';
  }
  return 'ignored';
}

/**
 * Subscribe to the study's realtime chat feed (GET /api/chat/studies/:studyId/stream).
 *
 * Hand-rolled SSE over fetch (mirrors streamCopilotQuery — EventSource cannot carry the
 * Authorization header). Dropped connections, failed attempts, and server-initiated
 * `stream_error` ends all reconnect automatically with exponential backoff (1s, 2s, 4s...
 * capped at 30s), and the backoff resets once a real event arrives. A fresh auth token is
 * fetched per attempt, so reconnection also survives token rotation.
 */
export function subscribeToStudyStream(
  studyId: string,
  handlers: StreamHandlers,
  options: StreamOptions = {},
): StreamSubscription {
  let stopped = false;
  let attempt = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let controller: AbortController | null = null;

  const stop = () => {
    if (stopped) return;
    stopped = true;
    if (reconnectTimer != null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    controller?.abort();
  };

  if (options.signal) {
    if (options.signal.aborted) stopped = true;
    else options.signal.addEventListener('abort', stop, { once: true });
  }

  const scheduleReconnect = () => {
    if (stopped || reconnectTimer != null) return;
    const delay = Math.min(STREAM_INITIAL_BACKOFF_MS * 2 ** attempt, STREAM_MAX_BACKOFF_MS);
    attempt += 1;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      void connect();
    }, delay);
  };

  const connect = async (): Promise<void> => {
    if (stopped) return;
    controller = new AbortController();
    let serverEnded = false;
    try {
      const token = await apiService.getAuthToken();
      const res = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.CHAT.STREAM(studyId)}`, {
        method: 'GET',
        headers: {
          Accept: 'text/event-stream',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        signal: controller.signal,
      });
      if (!res.ok || !res.body) {
        handlers.onStreamError?.(`Chat stream request failed (${res.status})`);
        scheduleReconnect();
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let sep: number;
        while ((sep = buffer.indexOf('\n\n')) >= 0) {
          const frame = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          const kind = dispatchStreamFrame(frame, handlers);
          if (kind === 'event') attempt = 0; // a live event proves the connection — reset backoff
          else if (kind === 'stream_error') serverEnded = true;
        }
        if (serverEnded) break; // the server ends the response after stream_error
      }
      if (!serverEnded && buffer.trim()) dispatchStreamFrame(buffer, handlers);
    } catch (error: any) {
      if (stopped || error?.name === 'AbortError') return;
      handlers.onStreamError?.(error?.message || 'Chat stream error');
    }
    if (!stopped) scheduleReconnect();
  };

  if (!stopped) void connect();

  return { unsubscribe: stop };
}
